import { dispatchManagementAuthRequired, readManagementToken } from '../auth/managementAuth';

export type ApiOptions = {
  token?: string;
  basePath?: string;
};

export type StatusResponse = {
  version: string;
  uptime_seconds: number;
  web_console: boolean;
};

type ApiEnvelope<T> = {
  ok?: boolean;
  data?: T;
  error?: string;
  message?: string;
};

export class ApiAuthError extends Error {
  readonly status: number;
  readonly path: string;

  constructor(message: string, status: number, path: string) {
    super(message);
    this.name = 'ApiAuthError';
    this.status = status;
    this.path = path;
  }
}

export class ManagementApi {
  private readonly basePath: string;
  private readonly token?: string;

  constructor(options: ApiOptions = {}) {
    this.basePath = options.basePath ?? '/api/v1';
    this.token = options.token ?? readManagementToken() ?? undefined;
  }

  async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const headers = new Headers(init.headers);
    headers.set('Accept', 'application/json');
    if (init.body && !headers.has('Content-Type')) {
      headers.set('Content-Type', 'application/json');
    }
    if (this.token) {
      headers.set('Authorization', `Bearer ${this.token}`);
    }

    const response = await fetch(`${this.basePath}${path}`, { ...init, headers });
    const payload = await response.json().catch(() => null) as ApiEnvelope<T> | T | null;
    if (!response.ok) {
      const envelope = payload as ApiEnvelope<T> | null;
      const message = envelope?.error || envelope?.message || response.statusText;
      if (response.status === 401 || response.status === 403) {
        dispatchManagementAuthRequired({ status: response.status, message, path });
        throw new ApiAuthError(message, response.status, path);
      }
      throw new Error(message);
    }
    if (payload && typeof payload === 'object' && 'ok' in payload && 'data' in payload) {
      return (payload as ApiEnvelope<T>).data as T;
    }
    return payload as T;
  }

  getStatus() {
    return this.request<StatusResponse>('/status');
  }

  getPersonas() {
    return this.request<unknown>('/personas');
  }

  getPersonaConfigs() {
    return this.request<unknown>('/persona_configs');
  }

  createPersonaConfig(body: unknown) {
    return this.request<unknown>('/persona_configs', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getPersona(personaId: string) {
    return this.request<unknown>(`/personas/${encodeURIComponent(personaId)}`);
  }

  getSkills() {
    return this.request<unknown>('/skills');
  }

  createSkill(body: unknown) {
    return this.request<unknown>('/skills', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getSkill(skillId: string) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}`);
  }

  updateSkillStatus(skillId: string, body: unknown) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  deleteSkill(skillId: string, actor = 'console') {
    const query = new URLSearchParams({ actor });
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}?${query.toString()}`, {
      method: 'DELETE',
    });
  }

  saveSkillDraft(skillId: string, body: unknown) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/draft`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  validateSkill(skillId: string) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/validate`, {
      method: 'POST',
    });
  }

  getSkillPreview(skillId: string) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/preview`);
  }

  getSkillVersions(skillId: string) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/versions`);
  }

  createSkillVersion(skillId: string, body: unknown) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/versions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  publishSkill(skillId: string, version: number, actor = 'console') {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ version, actor }),
    });
  }

  rollbackSkill(skillId: string, version: number, actor = 'console') {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/rollback`, {
      method: 'POST',
      body: JSON.stringify({ version, actor }),
    });
  }

  getSkillInstallations(skillId: string) {
    return this.request<unknown>(`/skills/${encodeURIComponent(skillId)}/installations`);
  }

  getSkillConflicts() {
    return this.request<unknown>('/skills/conflicts');
  }

  getPersonaConfig(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}`);
  }

  getPersonaConfigStatus(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/status`);
  }

  getPersonaConfigDiff(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/diff`);
  }

  getPersonaConfigPreview(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/preview`);
  }

  getPersonaConfigSnapshot(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/snapshot`);
  }

  getPersonaConfigVersions(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/versions`);
  }

  savePersonaConfigDraft(personaId: string, body: unknown) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/draft`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  validatePersonaConfig(personaId: string) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/validate`, {
      method: 'POST',
    });
  }

  createPersonaConfigVersion(personaId: string, changelog: string, actor = 'console') {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/versions`, {
      method: 'POST',
      body: JSON.stringify({ changelog, actor }),
    });
  }

  publishPersonaConfig(personaId: string, version: number, actor = 'console') {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ version, actor }),
    });
  }

  getPromptModuleAssignments(personaId: string, platform?: string) {
    const params = new URLSearchParams();
    if (platform) params.set('platform', platform);
    const query = params.toString();
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/prompt_module_assignments${query ? `?${query}` : ''}`);
  }

  updatePromptModuleAssignments(personaId: string, body: unknown) {
    return this.request<unknown>(`/persona_configs/${encodeURIComponent(personaId)}/prompt_module_assignments`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  getMCPs() {
    return this.request<unknown>('/mcps');
  }

  getMCP(mcpId: string) {
    return this.request<unknown>(`/mcps/${encodeURIComponent(mcpId)}`);
  }

  getMCPPreview(mcpId: string) {
    return this.request<unknown>(`/mcps/${encodeURIComponent(mcpId)}/preview`);
  }

  getMCPVersions(mcpId: string) {
    return this.request<unknown>(`/mcps/${encodeURIComponent(mcpId)}/versions`);
  }

  saveMCPDraft(mcpId: string, body: unknown) {
    return this.request<unknown>(`/mcps/${encodeURIComponent(mcpId)}/draft`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  createMCP(body: unknown) {
    return this.request<unknown>('/mcps', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  installMCPPreset(presetName: string, body: unknown) {
    return this.request<unknown>(`/mcp_presets/${encodeURIComponent(presetName)}/install`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getMCPConflicts() {
    return this.request<unknown>('/mcps/conflicts');
  }

  getMCPPresets() {
    return this.request<unknown>('/mcp_presets');
  }

  getCronJobs() {
    return this.request<unknown>('/cron');
  }

  getRelay() {
    return this.request<unknown>('/relay');
  }

  getSecrets() {
    return this.request<unknown>('/secrets');
  }

  getSystemOverview() {
    return this.request<unknown>('/system/overview');
  }


  getPromptModules() {
    return this.request<unknown>('/prompt_modules');
  }

  getPromptModule(moduleId: string) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}`);
  }

  getPromptModulePreview(moduleId: string) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/preview`);
  }

  getPromptModuleVersions(moduleId: string) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/versions`);
  }

  getPromptModuleConsumers(moduleId: string) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/consumers`);
  }

  createPromptModule(body: unknown) {
    return this.request<unknown>('/prompt_modules', {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  savePromptModuleDraft(moduleId: string, body: unknown) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/draft`, {
      method: 'PUT',
      body: JSON.stringify(body),
    });
  }

  updatePromptModuleStatus(moduleId: string, body: unknown) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}`, {
      method: 'PATCH',
      body: JSON.stringify(body),
    });
  }

  deletePromptModule(moduleId: string, actor = 'console') {
    const query = new URLSearchParams({ actor });
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}?${query.toString()}`, {
      method: 'DELETE',
    });
  }

  createBuiltinPromptModuleOverride(moduleName: string, body: unknown) {
    return this.request<unknown>(`/prompt_modules/builtins/${encodeURIComponent(moduleName)}/override`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  createPromptModuleVersion(moduleId: string, body: unknown) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/versions`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  publishPromptModule(moduleId: string, version: number, actor = 'console') {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/publish`, {
      method: 'POST',
      body: JSON.stringify({ version, actor }),
    });
  }

  rolloutPromptModule(moduleId: string, body: unknown) {
    return this.request<unknown>(`/prompt_modules/${encodeURIComponent(moduleId)}/rollout`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getEngagements(params: { personaId?: string; platform?: string; state?: string; limit?: string; cursor?: string } = {}) {
    const queryParams = new URLSearchParams({ limit: params.limit ?? '50' });
    if (params.personaId) queryParams.set('persona_id', params.personaId);
    if (params.platform) queryParams.set('platform', params.platform);
    if (params.state) queryParams.set('state', params.state);
    if (params.cursor) queryParams.set('cursor', params.cursor);
    return this.request<unknown>(`/engagements?${queryParams.toString()}`);
  }

  getEngagement(engagementId: string) {
    return this.request<unknown>(`/engagements/${encodeURIComponent(engagementId)}`);
  }

  getEngagementRelations(engagementId: string) {
    return this.request<unknown>(`/engagements/${encodeURIComponent(engagementId)}/relations`);
  }

  getEngagementState(engagementId: string) {
    return this.request<unknown>(`/engagements/${encodeURIComponent(engagementId)}/state`);
  }

  getLoopVerdict(params: { contractId?: string; workflowId?: string; engagementId?: string; issueId?: string }) {
    if (params.contractId && !params.workflowId && !params.engagementId && !params.issueId) {
      return this.request<unknown>(`/loop_verdicts/${encodeURIComponent(params.contractId)}`);
    }
    const query = new URLSearchParams();
    if (params.contractId) query.set('contract_id', params.contractId);
    if (params.workflowId) query.set('workflow_id', params.workflowId);
    if (params.engagementId) query.set('engagement_id', params.engagementId);
    if (params.issueId) query.set('issue_id', params.issueId);
    return this.request<unknown>(`/loop_verdicts?${query.toString()}`);
  }

  previewEngagementTransition(engagementId: string, body: unknown) {
    return this.request<unknown>(`/engagements/${encodeURIComponent(engagementId)}/transitions/preview`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  postEngagementControl(engagementId: string, action: string, body: unknown, operatorId: string) {
    return this.request<unknown>(`/engagements/${encodeURIComponent(engagementId)}/${encodeURIComponent(action)}`, {
      method: 'POST',
      headers: { 'X-Operator-ID': operatorId },
      body: JSON.stringify(body),
    });
  }

  getStateMachineFlow(flowId = 'engagement') {
    return this.request<unknown>(`/state_machine/flows/${encodeURIComponent(flowId)}`);
  }

  getEvents(params: { engagementId?: string; sessionId?: string; eventType?: string; limit?: string; cursor?: string } = {}) {
    return this.request<unknown>(`/events?${this.eventQuery(params).toString()}`);
  }

  getEventsStreamUrl(params: { engagementId?: string; personaId?: string; eventType?: string } = {}) {
    const query = new URLSearchParams();
    if (params.engagementId) query.set('engagement_id', params.engagementId);
    if (params.personaId) query.set('persona_id', params.personaId);
    if (params.eventType) query.set('event_type', params.eventType);
    if (this.token) query.set('token', this.token);
    const suffix = query.toString();
    return `${this.basePath}/events/stream${suffix ? `?${suffix}` : ''}`;
  }

  async downloadEventsExport(params: { format: 'ndjson' | 'json' | 'csv'; engagementId?: string; sessionId?: string; personaId?: string; eventType?: string }) {
    const query = new URLSearchParams({ format: params.format });
    if (params.engagementId) query.set('engagement_id', params.engagementId);
    if (params.sessionId) query.set('session_id', params.sessionId);
    if (params.personaId) query.set('persona_id', params.personaId);
    if (params.eventType) query.set('event_type', params.eventType);
    const headers = new Headers({ Accept: '*/*' });
    if (this.token) headers.set('Authorization', `Bearer ${this.token}`);
    const path = `/events/export?${query.toString()}`;
    const response = await fetch(`${this.basePath}${path}`, { headers });
    if (!response.ok) {
      const payload = await response.json().catch(() => null) as ApiEnvelope<unknown> | null;
      const message = payload?.error || payload?.message || response.statusText;
      if (response.status === 401 || response.status === 403) {
        dispatchManagementAuthRequired({ status: response.status, message, path });
        throw new ApiAuthError(message, response.status, path);
      }
      throw new Error(message);
    }
    const disposition = response.headers.get('Content-Disposition') ?? '';
    const fileName = disposition.match(/filename="?([^";]+)"?/i)?.[1] ?? `events.${params.format}`;
    return { blob: await response.blob(), fileName };
  }

  sendPersonaTestMessage(personaId: string, body: { session_key: string; message: string }) {
    return this.request<unknown>(`/personas/${encodeURIComponent(personaId)}/send`, {
      method: 'POST',
      body: JSON.stringify(body),
    });
  }

  getSessions(params: string | { personaId?: string; platform?: string; active?: string; limit?: string; cursor?: string } = {}) {
    const options = typeof params === 'string' ? { personaId: params, limit: '20' } : params;
    const queryParams = new URLSearchParams({ limit: options.limit ?? '50' });
    if (options.personaId) queryParams.set('persona_id', options.personaId);
    if (options.platform) queryParams.set('platform', options.platform);
    if (options.active) queryParams.set('active', options.active);
    if (options.cursor) queryParams.set('cursor', options.cursor);
    return this.request<unknown>(`/sessions?${queryParams.toString()}`);
  }

  getPersonaSessions(personaId: string) {
    const params = new URLSearchParams({ persona_id: personaId, limit: '20' });
    return this.request<unknown>(`/sessions?${params.toString()}`);
  }

  getSession(sessionId: string) {
    return this.request<unknown>(`/sessions/${encodeURIComponent(sessionId)}`);
  }

  getAudit(params: Record<string, string> = {}) {
    const query = new URLSearchParams(params);
    const suffix = query.toString();
    return this.request<unknown>(`/audit${suffix ? `?${suffix}` : ''}`);
  }

  private eventQuery(params: { engagementId?: string; sessionId?: string; eventType?: string; limit?: string; cursor?: string }) {
    const queryParams = new URLSearchParams({ limit: params.limit ?? '100' });
    if (params.engagementId) queryParams.set('engagement_id', params.engagementId);
    if (params.sessionId) queryParams.set('session_id', params.sessionId);
    if (params.eventType) queryParams.set('event_type', params.eventType);
    if (params.cursor) queryParams.set('cursor', params.cursor);
    return queryParams;
  }
}
